import { ChipPanel } from '@design-system/chip-panel';
import { GridContainer, GridItem } from '@design-system/grid';
import { SideFilters } from '@design-system/side-filters';
import { ScreenRecognitionContext } from '@phx-husky/screen-recognition';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { executeSearch } from '~/components/SearchResults/SearchService/executeSearch';
import { useDisclosure } from '~/hooks/useDisclosure';
import { useItemFilters } from '~/hooks/useFilter';
import { mapItems, wrapIfItsList } from '~/mappers/itemListing';
import {
  FilterMapperResult,
  ItemListingTypes,
} from '~/mappers/itemListing/models';
import { TypographyUI } from '~/mappers/typography/models';

import { ItemListingCounter } from '../ItemListingCounter/ItemListingCounter';
import { ItemListingCounterProps } from '../ItemListingCounter/models';
import ItemListingDefault from '../ItemListingDefault';
import ItemListingDefaultFilter from '../ItemListingDefault/ItemListingDefaultFilter';
import ItemListingDefaultSortBy from '../ItemListingDefault/ItemListingDefaultSortBy';
import { ItemListingDefaultProps } from '../ItemListingDefault/models';
import { ItemListingLoadMore } from '../ItemListingLoadMore/ItemListingLoadMore';
import { ItemListingLoadMoreProps } from '../ItemListingLoadMore/models';
import { normalizeItems } from '../ItemListingUtils';
import { FilterOption, ItemListingProps } from '../models';

const ItemListingResult = forwardRef<HTMLDivElement, ItemListingProps>(
  ({ type, itemsAmount = 0, ...props }, ref) => {
    const propsWithItemsAmount = { ...props, itemsAmount };

    if (itemsAmount === 0) {
      return (
        <ItemListingDefault
          ref={ref}
          {...(propsWithItemsAmount as ItemListingDefaultProps)}
        />
      );
    }

    switch (type) {
      case 'numbered':
        return (
          <ItemListingCounter
            ref={ref}
            {...(propsWithItemsAmount as ItemListingCounterProps)}
          />
        );
      case 'loadMore':
        return (
          <ItemListingLoadMore
            ref={ref}
            {...(propsWithItemsAmount as ItemListingLoadMoreProps)}
          />
        );
      case 'default':
      default:
        return (
          <ItemListingDefault
            ref={ref}
            {...(propsWithItemsAmount as ItemListingDefaultProps)}
          />
        );
    }
  }
);

const getItemName = (item) => {
  if (item.fields && item.fields.productData) {
    return item.fields.productData.productTitle;
  }

  if (item.productData?.productTitle) {
    return item.productData.productTitle;
  }

  return item.heading?.headline;
};

const defaultTypographyUI: TypographyUI = {
  variant: 'body',
  align: 'center',
  tag: 'p',
  size: 'm',
  weight: 'regular',
};

const ItemListingGrid = forwardRef<HTMLDivElement, ItemListingProps>(
  // eslint-disable-next-line max-statements
  (
    {
      pageBlock,
      fluidGrid,
      id,
      filterSettings = {} as FilterMapperResult,
      rawItems: initialRawItems,
      currentPageUid,
      uid,
      typeName,
      whereToBuyData,
      type,
      loadMoreButton,
      initialNumberArticles,
      content_type,
      selectEvents,
    },
    ref
  ) => {
    const {
      filtersTitle,
      filterSelection: {
        variant = FilterOption.TopFilters,
        showChipPanel = false,
        dropdownCategories,
      } = {},
      filterDialog: { openModal, closeModal, clearFilters } = {},
      filters,
      clearSelectedFilters,
      itemsName,
      sortBy,
      noResults: {
        text = 'No results found',
        typographyUI = defaultTypographyUI,
      } = {},
    } = filterSettings;

    const { itemsTotalCount } = initialRawItems;

    const [rawItems, setRawItems] = useState(initialRawItems);

    const [allLoaded, setAllLoaded] = useState(false);
    const fetchingAllRef = useRef(false);

    const handleAllItemsLoaded = () => {
      setAllLoaded(true);
    };

    const seenNewFullPaths = new Set<string>();

    const existingFullPaths = new Set(
      rawItems.items.map((item) => item.fullPath).filter(Boolean)
    );

    const handleAppendItems = (newItems) => {
      setRawItems((prev) => {
        const uniqueNewItems = newItems.filter((item) => {
          const { fullPath } = item;

          if (!fullPath) return true;
          if (existingFullPaths.has(fullPath)) return false;
          if (seenNewFullPaths.has(fullPath)) return false;

          seenNewFullPaths.add(fullPath);

          return true;
        });

        return {
          ...prev,
          items: [...prev.items, ...uniqueNewItems],
        };
      });
    };

    const { isOpen: isSortingOpen, toggle: toggleSortingOpen } =
      useDisclosure(false);
    const { isMobile } = useContext(ScreenRecognitionContext);

    const itemsWithProperties = useMemo(
      () =>
        rawItems.items.map((item) => ({
          ...item,
          name: getItemName(item),
          updatedAt: item.updatedAt || '',
        })),
      [rawItems.items]
    );

    const {
      data,
      filter,
      handleFilterChange,
      sort,
      handleSortChange,
      getActiveCountForGroup,
      handleGroupReset,
      handleClearAllFilters,
      handleActiveGroup,
      activeGroup,
    } = useItemFilters(itemsWithProperties, { filters });

    const items = wrapIfItsList({
      tag: fluidGrid.tag as string,
      items: mapItems(
        { ...rawItems, items: data } as ItemListingTypes,
        currentPageUid,
        whereToBuyData
      ),
    });

    const searchDataType =
      content_type === 'l2_event_page' ? 'event' : 'resource';
    const locale = 'en-au';

    const hasTriggeredRef = useRef(false);

    // eslint-disable-next-line max-statements, react-hooks/exhaustive-deps
    const ensureAllItemsLoaded = async () => {
      if (allLoaded || fetchingAllRef.current) return;
      if (!['event', 'resource'].includes(searchDataType)) return;

      fetchingAllRef.current = true;

      try {
        const searchResultsData = await executeSearch(searchDataType, locale);
        const searchResultsDataByType = searchResultsData[`${searchDataType}s`];

        const normalizedItems = normalizeItems(searchResultsDataByType);

        let processedItems = normalizedItems;

        if (searchDataType === 'event') {
          const now = Date.now();

          if (selectEvents === 'Past') {
            processedItems = normalizedItems.filter((item) => {
              const eventDate = new Date(
                item.eventTimestamp.event_start_date ?? 0
              ).getTime();

              return eventDate < now;
            });
          }

          if (selectEvents === 'Future') {
            processedItems = normalizedItems.filter((item) => {
              const eventDate = new Date(
                item.eventTimestamp.event_start_date ?? 0
              ).getTime();

              return eventDate > now;
            });
          }
        }

        const sortedItems = processedItems.sort((a, b) => {
          const getDate = (item) =>
            searchDataType === 'event'
              ? new Date(item.eventTimestamp.event_start_date ?? 0).getTime()
              : new Date(
                  item.pageAdditionalData.initial_publish_date ?? 0
                ).getTime();

          return getDate(b) - getDate(a);
        });

        const additionalItems = sortedItems.filter((item) => {
          const { fullPath } = item;

          if (!fullPath) return true;

          if (existingFullPaths.has(fullPath)) return false;

          if (seenNewFullPaths.has(fullPath)) return false;

          seenNewFullPaths.add(fullPath);

          return true;
        });

        setRawItems((prev) => ({
          ...prev,
          items: [...prev.items, ...additionalItems],
        }));

        setAllLoaded(true);
      } finally {
        fetchingAllRef.current = false;
      }
    };

    useEffect(() => {
      if (hasTriggeredRef.current) return;
      const hasActiveFilters = filter.length > 0;

      if (hasActiveFilters && typeName?.includes('all_item_listing_grid')) {
        hasTriggeredRef.current = true;
        ensureAllItemsLoaded();
      }
    }, [filter.length, typeName, ensureAllItemsLoaded]);

    if (
      variant === FilterOption.SideFilters &&
      (!openModal || !closeModal || !clearFilters)
    ) {
      console.warn(
        'Side filters were chosen, but props require openModal, closeModal and clearFilters to be passed as props.'
      );

      return null;
    }

    const itemsAmountToShow =
      filter.length > 0 ? data.length : rawItems.items.length;

    return filter && variant === FilterOption.SideFilters ? (
      <GridContainer className="cds-listing-filter" columnSpacing={4}>
        <GridItem md={3}>
          <SideFilters
            filters={filters}
            filter={filter}
            handleFilterChange={handleFilterChange}
            filtersTitle={filtersTitle}
            handleClearAllFilters={handleClearAllFilters}
            clearFilters={clearFilters!}
            closeModal={closeModal!}
            openModal={openModal!}
            itemsAmount={itemsAmountToShow}
            dropdownCategories={dropdownCategories}
          />
        </GridItem>
        <GridItem md={9}>
          <ChipPanel
            showChipPanel={showChipPanel}
            filter={filter}
            handleFilterChange={handleFilterChange}
          />
          <ItemListingResult
            typeName={typeName}
            pageBlock={pageBlock}
            itemsName={itemsName}
            itemsAmount={itemsAmountToShow}
            fluidGrid={fluidGrid}
            rawItems={rawItems}
            data={data}
            currentPageUid={currentPageUid}
            filters={filters}
            text={text}
            typographyUI={typographyUI}
            id={id}
            uid={uid}
            ref={ref}
            whereToBuyData={whereToBuyData}
            type={type}
            items={items}
            selectEvents={selectEvents}
            initialNumberArticles={
              initialNumberArticles === null ? 6 : initialNumberArticles
            }
            loadMoreButton={loadMoreButton}
            onItemsLoaded={handleAppendItems}
            content_type={content_type}
            allLoaded={allLoaded}
            onAllItemsLoaded={handleAllItemsLoaded}
          />
        </GridItem>
      </GridContainer>
    ) : (
      <>
        {filters && (
          <ItemListingDefaultFilter
            filters={filters}
            clearSelectedFilters={clearSelectedFilters}
            getActiveCountForGroup={getActiveCountForGroup}
            handleFilterChange={handleFilterChange}
            handleGroupReset={handleGroupReset}
            filter={filter}
            activeGroup={activeGroup}
            handleActiveGroup={handleActiveGroup}
            isMobile={isMobile}
          />
        )}
        {sortBy && (
          <ItemListingDefaultSortBy
            handleSortChange={handleSortChange}
            itemsAmount={itemsAmountToShow}
            isSortingOpen={isSortingOpen}
            toggleSortingOpen={toggleSortingOpen}
            sort={sort}
            itemsName={itemsName}
            sortBy={sortBy}
          />
        )}
        <ItemListingResult
          typeName={typeName}
          pageBlock={pageBlock}
          itemsName={itemsName}
          itemsAmount={itemsAmountToShow}
          fluidGrid={fluidGrid}
          rawItems={rawItems}
          data={data}
          currentPageUid={currentPageUid}
          filters={filters}
          text={text}
          typographyUI={typographyUI}
          id={id}
          uid={uid}
          ref={ref}
          whereToBuyData={whereToBuyData}
          type={type}
          items={items}
          selectEvents={selectEvents}
          initialNumberArticles={
            initialNumberArticles === null ? 6 : initialNumberArticles
          }
          loadMoreButton={loadMoreButton}
          onItemsLoaded={handleAppendItems}
          content_type={content_type}
          allLoaded={allLoaded}
          onAllItemsLoaded={handleAllItemsLoaded}
        />
      </>
    );
  }
);

export default ItemListingGrid;
